import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from '../../redux/hooks';
import InsuranceForm, { ShippingSummary } from '../../components/insurance-form/InsuranceForm';
import ShippingPreview from './shipping-preview/ShippingPreview';
import { BackButton, DateInput, DotLoader, Loader } from '../../components';
import { ValidatedState } from '../../components/custom-input/ValidatedInput';
import ValidatedPickupInfo, { PickupInfo } from '../../components/custom-input/ValidatedPickupInfo';
import { InfoSVG } from '../../components/svgs';
import TrashCanSvg from '../../components/svgs/TrashCan.svg';
import ValidatedAddress from '../../components/custom-input/ValidatedAddress';
import { FilterOption } from '../../components/custom-input/FilterDropdown';
import InsuranceTermsModal from './insurance-terms-conditions/InsuranceTermsModal';
import SpecimenLotInfo from '../../components/specimen-lot-info/SpecimenLotInfo';
import { RequireAlert } from '../inventory-management/InventoryManagementCollectDeposit';
import { EmbryoStraw } from '../../types/interfaces';
import { TransactionTypeEnum } from '../../types/enums';
import { CartTransactionResponse, SpecimenCartItem } from '../../types/interfaces/transactionCart.interfaces';
import { withdrawInventory } from '../../api/inventoryApi';
import { getAccountById } from '../../api/accountApi';
import { getShippingQuoteForOrder } from '../../api/shipmentsApi';
import {
  deleteSpecimenFromTransactionCart,
  deleteTransactionCartItem,
  getCartCompletionTransaction,
} from '../../api/transactionCartApi';
import { getEmbryoStraws } from '../../api/embryoStrawsApi';
import { showToast } from '../../services/toast.service';
import { PermissionService } from '../../services/permission.service';
import { fetchTransactionCartItems } from '../../services/transactionCart.service';
import { formatPositiveNumber, normalizePayload } from '../../utils/commonUtils';
import { getPickupNotes } from '../../utils/addressHelpers';
import { toastMessages } from '../../constants/errorMessages';
import { ROUTE_PATHS } from '../../constants/routePaths';
import {
  BUTTON_CONSTANTS,
  INVENTORY_CONSTANTS,
  INVENTORY_MANAGEMENT_CONSTANTS,
  LABEL_CONSTANTS,
  PAGE_HEADER_CONSTANTS,
  TABLE_HEADER_CONSTANTS,
} from '../../constants/common';
import './transactionCart.scss';

export interface ValidateAddress {
  addressId?: ValidatedState;
  streetAddress: ValidatedState;
  streetAddress2: ValidatedState;
  city: ValidatedState;
  state: FilterOption<string>;
  postalCode: ValidatedState;
  country: FilterOption<string>;
  phone?: ValidatedState;
}

type CustomerInfoForm = {
  customerName: ValidatedState;
  customerEmail: ValidatedState;
  customerPhoneNumber: ValidatedState;
};

type TransactionCartWithdrawProps = {
  permissionService: PermissionService;
};

export const defaultShippingSummary: ShippingSummary = {
  shippingInsurance: '',
  insuranceAmount: { value: '', valid: false },
  insurance: 0,
  termsAndConditions: '',
  handlingProcessingFee: 0,
  tankRentalFee: 0,
  shippingOutBoundCost: 0,
  shippingReturnCost: 0,
};

const TransactionCartWithdraw = ({ permissionService }: TransactionCartWithdrawProps) => {
  const navigate = useNavigate();
  const { accountId } = useParams();
  const initialFormState = { value: '', valid: false };

  const user = useAppSelector(state => state.user);
  const paymentStatus = useAppSelector(state => state.payment.addedPaymentMethod);
  const activeAccount = useAppSelector(state => state.activeAccount);

  const [withdrawCartItem, setWithdrawCartItem] = useState<CartTransactionResponse>();
  const [withdrawMethodIsShip, setWithdrawMethodIsShip] = useState<boolean>();
  const [notes, setNotes] = useState<string>('');
  const [address, setAddress] = useState<ValidateAddress>();
  const [addressValid, setAddressValid] = useState<boolean>();
  const [pickupInfo, setPickupInfo] = useState<PickupInfo>();
  const [pickupInfoValid, setPickupInfoValid] = useState<boolean>();
  const [validate, setValidate] = useState<boolean>(false);
  const [rentDryVaporShipperTank, setRentDryVaporShipperTank] = useState<string>('');
  const [withdrawingInventory, setWithdrawingInventory] = useState<boolean>(true);
  const [fetchingShippingCost, setFetchingShippingCost] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [shippingInsuranceValid, setShippingInsuranceValid] = useState<boolean>(false);
  const [breedDate, setBreedDate] = useState<Date>();
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [shippingSummary, setShippingSummary] = useState<ShippingSummary>(defaultShippingSummary);
  const [customerInfo, setCustomerInfo] = useState<CustomerInfoForm>({
    customerName: initialFormState,
    customerEmail: initialFormState,
    customerPhoneNumber: initialFormState,
  });
  const [insuranceTermsModalOpen, setInsuranceTermsModalOpen] = useState(false);
  const [embryoStraws, setEmbryoStraws] = useState<EmbryoStraw[]>([]);

  const loadCartAndStraws = useCallback(async () => {
    setLoading(true);
    try {
      const strawParams = { filter: `AccountId eq ${accountId};TransactionCartItemId neq NULL` };
      const [strawsResponse, cartResponse] = await Promise.all([
        accountId ? getEmbryoStraws(strawParams) : Promise.resolve({ data: [] }),
        user?.userId && accountId
          ? getCartCompletionTransaction(user.userId, Number(accountId), TransactionTypeEnum.Withdraw, {
              include: 'EmbryoStraw,SpecimenCustomDataValue',
            })
          : Promise.resolve({ data: undefined }),
      ]);
      const straws: EmbryoStraw[] = normalizePayload<EmbryoStraw>(strawsResponse?.data);
      const cart: CartTransactionResponse | undefined = cartResponse && cartResponse.data;
      setEmbryoStraws(straws);
      if (!cart) {
        setWithdrawCartItem(undefined);
        setLoading(false);
        return;
      }
      const mergedCart: CartTransactionResponse = {
        ...cart,
        specimenCartItems: (cart.specimenCartItems || []).map(specimenItem => {
          const matchingEmbryoStraws = straws.filter(
            straw => Number(straw.transactionCartItemId) === Number(specimenItem.transactionCartItemId),
          );

          return {
            ...specimenItem,
            specimen: {
              ...specimenItem.specimen,
              embryoStraws: matchingEmbryoStraws,
            },
          };
        }),
      };

      setWithdrawCartItem(mergedCart);
    } catch (err) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      setWithdrawCartItem(undefined);
    } finally {
      setLoading(false);
    }
  }, [user, accountId]);

  useEffect(() => {
    loadCartAndStraws();
  }, [loadCartAndStraws]);

  useEffect(() => {
    if (!withdrawCartItem) return;
    setWithdrawCartItem(prev => {
      if (!prev) return prev;
      const updatedItems = prev.specimenCartItems.map(item => {
        const matchingStraws = embryoStraws.filter(
          straw => Number(straw.transactionCartItemId) === Number(item.transactionCartItemId),
        );
        return {
          ...item,
          specimen: {
            ...item.specimen,
            embryoStraws: matchingStraws,
          },
        };
      });
      return { ...prev, specimenCartItems: updatedItems };
    });
  }, [embryoStraws]);

  useEffect(() => {
    const getCustomerAccountInfo = async () => {
      if (accountId) {
        try {
          const { data: accountResponse } = await getAccountById(Number(accountId));
          setCustomerInfo({
            customerName: { value: accountResponse?.contactFirstName + ' ' + accountResponse?.contactLastName, valid: true },
            customerEmail: { value: accountResponse?.contactEmail, valid: true },
            customerPhoneNumber: { value: accountResponse?.contactPhone, valid: true },
          });
        } catch (error) {
          showToast.error(toastMessages.SOMETHING_WENT_WRONG);
        }
      }
    };

    getCustomerAccountInfo();
  }, [accountId]);

  const totalDiscardQuantity = withdrawCartItem?.specimenCartItems?.reduce(
    (accumulator: number, specimenCartItem) => accumulator + specimenCartItem?.quantity,
    0,
  );

  const getEmbryoCartItemsMissingStraws = () =>
    (withdrawCartItem?.specimenCartItems ?? []).filter(item => {
      const isFemale = item?.animal?.gender?.toLowerCase() === 'female';
      return isFemale && (item?.specimen?.embryoStraws?.length ?? 0) === 0;
    });

  const isQuantityValid = (showToasts?: boolean) => {
    const missingStrawItems = getEmbryoCartItemsMissingStraws();
    if (missingStrawItems.length > 0) {
      showToasts && showToast.error(toastMessages.EMBRYO_CART_MISSING_STRAW_DETAILS);
      return false;
    }
    if (
      withdrawCartItem?.specimenCartItems.length === 0 ||
      withdrawCartItem?.specimenCartItems.some(specimenCartItem => specimenCartItem.quantity === 0)
    ) {
      showToasts && showToast.error(toastMessages.INVALID_ZERO_QUANTITY);
      return false;
    } else if (
      withdrawCartItem?.specimenCartItems.some(
        specimenCartItem => specimenCartItem.quantity > specimenCartItem?.availableQuantity,
      )
    ) {
      showToasts && showToast.error(toastMessages.CANT_WITHDRAW_MORE_THAN_AVAIL_QTY);
      return false;
    } else {
      return true;
    }
  };

  const checkPaymentInfoValid = () => {
    if (permissionService.userHasRSGAdminPermission()) {
      return true;
    } else {
      return paymentStatus != undefined && paymentStatus && activeAccount?.billingAmount?.outstandingBalanceCents === 0;
    }
  };

  const isAdditionalInfoValid = () => {
    return (
      checkPaymentInfoValid() &&
      ((withdrawMethodIsShip && shippingInsuranceValid && rentDryVaporShipperTank.length > 0) || !withdrawMethodIsShip) &&
      breedDate != undefined &&
      breedDate &&
      ((withdrawMethodIsShip && addressValid) || (!withdrawMethodIsShip && pickupInfoValid))
    );
  };

  const onWithdrawQtyChange = (e: any, index: number) => {
    const count = formatPositiveNumber(e.target.value);
    if (withdrawCartItem) {
      let newSpecimens = { ...withdrawCartItem };
      newSpecimens.specimenCartItems[index].quantity = count;
      setWithdrawCartItem(newSpecimens);
    }
  };

  const removeTransactionCartItem = async (accountId: number) => {
    try {
      await deleteTransactionCartItem(user?.userId, accountId, TransactionTypeEnum.Withdraw);
      handleTransactionCart();
    } catch (error) {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  };

  const handleSpecimenDelete = async (e: any, index: number) => {
    const transactionCartItemId = withdrawCartItem?.specimenCartItems[index]?.transactionCartItemId;
    if (transactionCartItemId) {
      try {
        await deleteSpecimenFromTransactionCart(transactionCartItemId);
        loadCartAndStraws();
        handleTransactionCart();
      } catch (error: any) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
    }
  };

  const handleTransactionCart = () => {
    if (permissionService.userHasRSGAdminPermission()) {
      fetchTransactionCartItems(user.userId);
    } else {
      fetchTransactionCartItems(user.userId, Number(accountId));
    }
  };

  const handleErrors = (specimenCartItem: SpecimenCartItem) => {
    return specimenCartItem.quantity > specimenCartItem?.availableQuantity
      ? 'error'
      : specimenCartItem.quantity === 0
      ? 'warning'
      : '';
  };

  const inputQuantityElement = (specimenCartItem: SpecimenCartItem, index: number) => {
    return <input type="text" placeholder="0" value={specimenCartItem.quantity} onChange={e => onWithdrawQtyChange(e, index)} />;
  };

  const handlePickUpWithdrawSubmit = async (e: React.FormEvent) => {
    if (!withdrawingInventory) return;

    setWithdrawingInventory(false);
    setValidate(true);

    if (isAdditionalInfoValid() && isQuantityValid(true)) {
      const groupedSpecimensMap = new Map<number, { quantity: number; selectedEmbryoStrawIds?: number[] }>();

      withdrawCartItem?.specimenCartItems.forEach(item => {
        const isFemale = item?.animal?.gender?.toLowerCase() === 'female';
        const specimenId = item.specimenId;

        if (isFemale && item.specimen?.embryoStraws?.length) {
          const strawIds = item.specimen.embryoStraws.map(specimen => specimen.embryoStrawId);

          if (!groupedSpecimensMap.has(specimenId)) {
            groupedSpecimensMap.set(specimenId, {
              quantity: item.quantity || 0,
              selectedEmbryoStrawIds: strawIds,
            });
          } else {
            const existing = groupedSpecimensMap.get(specimenId)!;
            existing.quantity += item.quantity || 0;
            existing.selectedEmbryoStrawIds = [...(existing.selectedEmbryoStrawIds ?? []), ...strawIds];
          }
        } else {
          if (!groupedSpecimensMap.has(specimenId)) {
            groupedSpecimensMap.set(specimenId, { quantity: item.quantity || 0 });
          } else {
            const existing = groupedSpecimensMap.get(specimenId)!;
            existing.quantity += item.quantity || 0;
          }
        }
      });

      const specimensSelected = Array.from(groupedSpecimensMap.entries()).map(([specimenId, data]) => ({
        specimenId,
        quantity: data.quantity,
        ...(data.selectedEmbryoStrawIds ? { selectedEmbryoStrawIds: data.selectedEmbryoStrawIds } : {}), // only include if present
      }));

      const withdrawRequestBody = {
        animalSelections: [{ specimens: specimensSelected }],
        breedDate: breedDate!,
        notes: notes + '\nBreed Date: ' + breedDate?.toLocaleDateString('en-US') + getPickupNotes(pickupInfo),
      };

      try {
        await withdrawInventory(withdrawRequestBody, accountId!, false);
        showToast.success(toastMessages.WITHDRAW_SUBMITTED);
        await removeTransactionCartItem(Number(accountId));
        navigate(ROUTE_PATHS.APP_TRANSACTION_CART);
      } catch {
        showToast.error(toastMessages.WITHDRAW_SUBMIT_FAIL);
      } finally {
        setWithdrawingInventory(true);
      }
    } else {
      setWithdrawingInventory(true);
    }
  };

  const fetchShippingCost = async () => {
    if (addressValid && address) {
      setFetchingShippingCost(false);
      const { phone, streetAddress, city, state, postalCode, country } = address;

      const buildPayload = (isReturn: boolean) => ({
        name: withdrawCartItem?.account.name,
        phone: phone?.value,
        addressLine: streetAddress.value,
        toCity: city.value,
        toState: state.value,
        toPostalCode: postalCode.value,
        toCountry: country.value,
        isReturn,
      });

      let outBoundCost = 0;
      let returnCost = 0;

      if (rentDryVaporShipperTank === LABEL_CONSTANTS.YES) {
        const { data: outboundLabelQuote } = await getShippingQuoteForOrder(Number(accountId), buildPayload(false));
        outBoundCost = outboundLabelQuote.totalCost || 0;

        const { data: returnLabelQuote } = await getShippingQuoteForOrder(Number(accountId), buildPayload(true));
        returnCost = returnLabelQuote.totalCost || 0;
      }

      setShippingSummary(prevSummary => ({ ...prevSummary, shippingOutBoundCost: outBoundCost, shippingReturnCost: returnCost }));
    }
  };

  const handleCheckout = async (e: React.FormEvent) => {
    if (!fetchingShippingCost) return;
    setValidate(true);

    try {
      if (isAdditionalInfoValid() && isQuantityValid(true)) {
        setFetchingShippingCost(false);
        await fetchShippingCost();
        if (shippingSummary?.shippingInsurance === LABEL_CONSTANTS.YES) {
          if (shippingSummary.termsAndConditions === LABEL_CONSTANTS.ACCEPTED) {
            setIsPreview(true);
          } else {
            setInsuranceTermsModalOpen(true);
          }
        } else {
          setShippingSummary(prevSummary => ({ ...prevSummary, termsAndConditions: LABEL_CONSTANTS.DECLINED }));
          setIsPreview(true);
        }
      }
    } catch (error: any) {
      if (error.response?.status === 400) {
        showToast.error(toastMessages.FAILED_TO_RETRIEVE_SHIPPING_COST);
      } else if (error.response?.status === 401) {
        showToast.error(toastMessages.UNAUTHORIZED_TO_CREATE_TRANSACTION);
      } else {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      }
    } finally {
      setFetchingShippingCost(true);
    }
  };

  const handleConfirm = async () => {
    setShippingSummary({ ...shippingSummary, termsAndConditions: LABEL_CONSTANTS.ACCEPTED });
    setInsuranceTermsModalOpen(false);
    setIsPreview(true);
  };

  return (
    <div>
      {!isPreview && <BackButton pagesBack={2} />}
      <div className="inventory-action card">
        <h1>{PAGE_HEADER_CONSTANTS.CREATE_WITHDRAW_TRANSACTION}</h1>
        {!isPreview ? (
          <div className="transaction-cart-withdraw">
            <div className="form-row">
              <label>{LABEL_CONSTANTS.ACCOUNT_OWNER_ID}:</label>
              <div className="input-container">
                <p>{withdrawCartItem?.account?.name ?? '--'}</p>
              </div>
            </div>
            <br />

            <h3>{LABEL_CONSTANTS.INVENTORY_ITEMS}</h3>
            {!loading &&
              (() => {
                const missingStrawItems = getEmbryoCartItemsMissingStraws();
                if (missingStrawItems.length === 0) return null;
                return (
                  <div className="info-banner">
                    <InfoSVG size="18" color="#0f64dcf5" />
                    {INVENTORY_CONSTANTS.EMBRYO_MISSING_STRAW_DETAILS_MESSAGE(
                      `${missingStrawItems
                        .map(item => item.specimenId)
                        .slice(0, 4)
                        .join(', ')}${missingStrawItems.length > 4 ? '...' : ''}`,
                    )}
                  </div>
                );
              })()}
            <div className="desk-select-specimen">
              <div className="specimen-table">
                <table>
                  <thead>
                    <tr>
                      <th>{TABLE_HEADER_CONSTANTS.ANIMAL}</th>
                      <th>{TABLE_HEADER_CONSTANTS.INVENTORY_TYPE}</th>
                      <th>{TABLE_HEADER_CONSTANTS.LOT_DATE_NO}</th>
                      <th>{TABLE_HEADER_CONSTANTS.STRAW_LOT}</th>
                      <th>{TABLE_HEADER_CONSTANTS.STRAW_NUMBER}</th>
                      <th>{TABLE_HEADER_CONSTANTS.AVAILABLE_QTY}</th>
                      <th>{TABLE_HEADER_CONSTANTS.QTY_TO_WITHDRAW}</th>
                      <th>{TABLE_HEADER_CONSTANTS.ACTION}</th>
                    </tr>
                  </thead>

                  <tbody>
                    {!loading &&
                      withdrawCartItem?.specimenCartItems?.flatMap((specimenCartItem, index) => {
                        const isFemale = specimenCartItem?.animal?.gender?.toLowerCase() === 'female';
                        const embryoStraws = specimenCartItem?.specimen?.embryoStraws ?? [];

                        if (isFemale && embryoStraws.length > 0) {
                          return embryoStraws.map(straw => (
                            <tr
                              className={handleErrors(specimenCartItem)}
                              key={`female-straw-${specimenCartItem.specimenId}-${straw.embryoStrawId}`}>
                              <td>
                                {specimenCartItem.animal?.code} - {specimenCartItem.animal?.name}
                              </td>
                              <td>{specimenCartItem.specimen.specimenType?.name}</td>
                              <td>
                                <SpecimenLotInfo
                                  specimenId={specimenCartItem.specimen.specimenId}
                                  lotNumber={specimenCartItem.specimen.lotNumber}
                                  freezeDate={specimenCartItem.specimen.freezeDate}
                                />
                              </td>
                              <td>{straw.embryoStrawCode ?? '-'}</td>
                              <td>{straw.embryoStrawNumber ?? '-'}</td>
                              <td>{straw.embryoCountPerStraw ?? '-'}</td>
                              <td>
                                <input type="text" placeholder="0" value={straw.embryoCountPerStraw ?? 0} disabled />
                              </td>
                              <td>
                                <button className="trashIcon button" onClick={e => handleSpecimenDelete(e, index)}>
                                  <TrashCanSvg />
                                </button>
                              </td>
                            </tr>
                          ));
                        }

                        return (
                          <tr
                            className={handleErrors(specimenCartItem)}
                            key={`normal-specimen-${specimenCartItem.specimenId}-${index}`}>
                            <td>
                              {specimenCartItem.animal?.code} - {specimenCartItem.animal?.name}
                            </td>
                            <td>{specimenCartItem.specimen.specimenType?.name}</td>
                            <td>
                              <SpecimenLotInfo
                                specimenId={specimenCartItem.specimen.specimenId}
                                lotNumber={specimenCartItem.specimen.lotNumber}
                                freezeDate={specimenCartItem.specimen.freezeDate}
                              />
                            </td>
                            <td>-</td>
                            <td>-</td>
                            <td>{specimenCartItem.availableQuantity}</td>
                            <td>{inputQuantityElement(specimenCartItem, index)}</td>
                            <td>
                              <button className="trashIcon button" onClick={e => handleSpecimenDelete(e, index)}>
                                <TrashCanSvg />
                              </button>
                            </td>
                          </tr>
                        );
                      })}

                    {!loading && withdrawCartItem?.specimenCartItems.length === 0 && (
                      <tr>
                        <td colSpan={8}>{LABEL_CONSTANTS.NO_INVENTORY_FOUND}</td>
                      </tr>
                    )}

                    {loading && (
                      <tr>
                        <td colSpan={8}>
                          <Loader loaderSize="small" />
                        </td>
                      </tr>
                    )}

                    <tr>
                      <td colSpan={7}>{LABEL_CONSTANTS.TOTAL_QUANTITY}:</td>
                      <td>
                        <input type="text" disabled value={totalDiscardQuantity} />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="xs-select-specimens">
              {loading ? (
                <Loader addedSpace loaderSize="small" />
              ) : !loading && withdrawCartItem?.specimenCartItems.length === 0 ? (
                <div className="result-item">{`${LABEL_CONSTANTS.NO_RESULTS_FOUND}.`}</div>
              ) : (
                <div>
                  {withdrawCartItem &&
                    withdrawCartItem.specimenCartItems.flatMap((specimenCartItem, index: number) => {
                      const isFemale = specimenCartItem?.animal?.gender?.toLowerCase() === 'female';
                      const embryoStraws = specimenCartItem?.specimen?.embryoStraws ?? [];

                      if (isFemale && embryoStraws.length > 0) {
                        return embryoStraws.map(straw => (
                          <div
                            className={`card max-width ${handleErrors(specimenCartItem)}`}
                            key={`female-specimen-${specimenCartItem.specimenId}-${straw.embryoStrawId}`}>
                            <div className="selected-specimens-row">
                              <div className="select-specimens">
                                <div className="card-content-section">
                                  <div className="left column">
                                    <label>{`${TABLE_HEADER_CONSTANTS.ANIMAL}: `}</label>
                                    <label>
                                      {specimenCartItem.animal?.code} - {specimenCartItem.animal?.name}
                                    </label>
                                  </div>

                                  <div className="left column">
                                    <label>{`${TABLE_HEADER_CONSTANTS.INVENTORY_TYPE}: `}</label>
                                    <label>{specimenCartItem.specimen.specimenType?.name}</label>
                                  </div>

                                  <div className="left column">
                                    <label>{`${TABLE_HEADER_CONSTANTS.LOT_DATE_NO}: `}</label>
                                    <label>
                                      <SpecimenLotInfo
                                        specimenId={specimenCartItem.specimen.specimenId}
                                        lotNumber={specimenCartItem.specimen.lotNumber}
                                        freezeDate={specimenCartItem.specimen.freezeDate}
                                      />
                                    </label>
                                  </div>

                                  <div className="right column">
                                    <label>{`${TABLE_HEADER_CONSTANTS.STRAW_LOT}: `}</label>
                                    <label>{straw.embryoStrawCode ?? '-'}</label>
                                  </div>
                                </div>

                                <div className="card-content-section">
                                  <div className="left column">
                                    <label>{`${TABLE_HEADER_CONSTANTS.STRAW_NUMBER}: `}</label>
                                    <label>{straw.embryoStrawNumber ?? '-'}</label>
                                  </div>

                                  <div className="left column">
                                    <label>{`${TABLE_HEADER_CONSTANTS.AVAILABLE_QTY}: `}</label>
                                    <label>{straw.embryoCountPerStraw ?? '-'}</label>
                                  </div>

                                  <div className="left column">
                                    <label>{`${TABLE_HEADER_CONSTANTS.QTY_TO_WITHDRAW}: `}</label>
                                    <input type="text" placeholder="0" value={straw.embryoCountPerStraw ?? 0} disabled />
                                  </div>

                                  <div className="right center column">
                                    <label>{`${TABLE_HEADER_CONSTANTS.ACTION}:`}</label>
                                    <button className="trashIcon button" onClick={e => handleSpecimenDelete(e, index)}>
                                      <TrashCanSvg />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ));
                      }
                      return (
                        <div
                          className={`card max-width ${handleErrors(specimenCartItem)}`}
                          key={`male-specimen-${specimenCartItem.specimenId}-${index}`}>
                          <div className="selected-specimens-row">
                            <div className="select-specimens">
                              <div className="card-content-section">
                                <div className="left column">
                                  <label>{`${TABLE_HEADER_CONSTANTS.ANIMAL}: `}</label>
                                  <label>{`${specimenCartItem.animal?.code} - ${specimenCartItem.animal?.name}`}</label>
                                </div>
                                <div className="left column">
                                  <label>{`${TABLE_HEADER_CONSTANTS.INVENTORY_TYPE}: `}</label>
                                  <label>{`${specimenCartItem.specimen.specimenType?.name}`}</label>
                                </div>
                                <div className="left column">
                                  <label>{`${TABLE_HEADER_CONSTANTS.LOT_DATE_NO}: `}</label>
                                  <label>
                                    {
                                      <SpecimenLotInfo
                                        specimenId={specimenCartItem.specimen.specimenId}
                                        lotNumber={specimenCartItem.specimen.lotNumber}
                                        freezeDate={specimenCartItem.specimen.freezeDate}
                                      />
                                    }
                                  </label>
                                </div>
                              </div>
                              <div className="card-content-section">
                                <div className="left column">
                                  <label>{`${TABLE_HEADER_CONSTANTS.AVAILABLE_QTY}: `}</label>
                                  <label>{`${specimenCartItem.availableQuantity}`}</label>
                                </div>
                                <div className="left column">
                                  <label>{`${TABLE_HEADER_CONSTANTS.QTY_TO_WITHDRAW}: `}</label>
                                  <label>{inputQuantityElement(specimenCartItem, index)}</label>
                                </div>
                                <div className="right center column">
                                  <label>{`${TABLE_HEADER_CONSTANTS.ACTION}:`}</label>
                                  <button className={'trashIcon button'} onClick={e => handleSpecimenDelete(e, index)}>
                                    <TrashCanSvg />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  <div className="total-quantity">
                    <label>{LABEL_CONSTANTS.TOTAL_QUANTITY}: &nbsp; </label>
                    {totalDiscardQuantity}
                  </div>
                </div>
              )}
            </div>

            <br />

            <div className="form-row">
              <label>
                *{LABEL_CONSTANTS.BREED_DATE}:
                <RequireAlert isActive={validate && !breedDate} />
              </label>
              <DateInput value={breedDate} onChange={setBreedDate} isClearable minDate={new Date()} maxDate={null} />
            </div>

            <div className="form-row expanded-text-area">
              <label htmlFor="inventory-notes-textarea">{LABEL_CONSTANTS.NOTES}:</label>
              <textarea
                value={notes}
                placeholder="Notes"
                id="inventory-notes-textarea"
                onChange={e => setNotes(e.target.value)}
              />
            </div>
            <br />
            <div className="form-row">
              <label>{LABEL_CONSTANTS.WITHDRAW_METHOD}:</label>
              <div className="radio-row">
                &ensp;
                <input
                  type="radio"
                  id="shipping"
                  value="true"
                  checked={withdrawMethodIsShip}
                  onChange={() => setWithdrawMethodIsShip(true)}
                />
                <label htmlFor="shipping">{LABEL_CONSTANTS.SHIP_TO_ADDRESS}</label>
                <input
                  type="radio"
                  id="pickup"
                  value="false"
                  checked={withdrawMethodIsShip === false}
                  onChange={() => setWithdrawMethodIsShip(false)}
                />
                <label htmlFor="pickup">{LABEL_CONSTANTS.PICKUP}</label>
              </div>
            </div>

            {(!withdrawingInventory || !fetchingShippingCost) && <DotLoader />}

            {withdrawMethodIsShip === true && (
              <>
                <div className="form-row rent-tank">
                  <label className="rent-tank-radio-row-label">
                    <div>
                      *{INVENTORY_CONSTANTS.HAVE_TANK}:
                      <RequireAlert isActive={validate && !rentDryVaporShipperTank} />
                    </div>
                  </label>
                  <div className=" rent-tank-radio-row radio-row">
                    &ensp;
                    <input
                      type="radio"
                      name="nitrogen-tank"
                      checked={rentDryVaporShipperTank === LABEL_CONSTANTS.YES}
                      onChange={() => setRentDryVaporShipperTank(LABEL_CONSTANTS.YES)}
                    />
                    <label>{LABEL_CONSTANTS.YES_NEED_TO_RENT_TANK}</label>
                    <input
                      type="radio"
                      name="nitrogen-tank"
                      checked={rentDryVaporShipperTank === LABEL_CONSTANTS.NO}
                      onChange={() => setRentDryVaporShipperTank(LABEL_CONSTANTS.NO)}
                    />
                    <label>{LABEL_CONSTANTS.NO_HAVE_MY_OWN_TANK}</label>
                  </div>
                </div>

                <h3>{LABEL_CONSTANTS.SHIPPING_INFO}</h3>
                <p>{INVENTORY_MANAGEMENT_CONSTANTS.WITHDRAW_SHIPPING_NOTE}</p>
                <br />
                <ValidatedAddress validate={validate} setValid={setAddressValid} setAddress={setAddress} address={address} />
                <br />
                <InsuranceForm
                  validate={validate}
                  setValid={setShippingInsuranceValid}
                  setShippingSummary={setShippingSummary}
                  rentTank={rentDryVaporShipperTank}
                  shippingSummary={shippingSummary}
                  promptLabel={INVENTORY_CONSTANTS.ADD_INSURANCE_PROMPT}
                />
                {shippingSummary.shippingInsurance === LABEL_CONSTANTS.YES &&
                  shippingSummary.termsAndConditions === LABEL_CONSTANTS.ACCEPTED && (
                    <div className="form-row">
                      <label>{LABEL_CONSTANTS.TERMS_AND_CONDITIONS}:</label>
                      <div className="input-container">
                        <p>{shippingSummary.termsAndConditions ?? '--'}</p>
                      </div>
                    </div>
                  )}
              </>
            )}

            {withdrawMethodIsShip === false && (
              <>
                <ValidatedPickupInfo validate={validate} setValid={setPickupInfoValid} setPickupInfo={setPickupInfo} />
                <br />
                <div className="flex-right withdraw-submit-section">
                  <p className="withdraw-inventory-guide withdraw-submit-guide">
                    {INVENTORY_MANAGEMENT_CONSTANTS.COMMON_INVENTORY_FOOTER}
                  </p>
                </div>
              </>
            )}

            <div className="flex-right margin-top">
              <button
                type="submit"
                className={`button green ${withdrawMethodIsShip ? 'large' : 'small'} ${
                  !isQuantityValid() || !isAdditionalInfoValid() ? 'disabled' : ''
                }`}
                onClick={withdrawMethodIsShip ? handleCheckout : handlePickUpWithdrawSubmit}>
                {withdrawMethodIsShip ? BUTTON_CONSTANTS.PROCEED_TO_CHECKOUT : BUTTON_CONSTANTS.SUBMIT}
              </button>
            </div>
          </div>
        ) : (
          <ShippingPreview
            withdrawCartItem={withdrawCartItem}
            shippingAddress={address}
            shippingSummary={shippingSummary}
            customerInfo={customerInfo}
            setIsPreview={setIsPreview}
            setCustomerInfo={setCustomerInfo}
            breedDate={breedDate}
            rentDryVaporShipperTank={rentDryVaporShipperTank}
            notes={notes}
            permissionService={permissionService}
          />
        )}
        <InsuranceTermsModal
          isOpen={insuranceTermsModalOpen}
          onClose={() => setInsuranceTermsModalOpen(false)}
          onAccept={() => handleConfirm()}
        />
      </div>
    </div>
  );
};

export default TransactionCartWithdraw;
